import { IFormInputSpec } from 'pages/revisions-module/template-editor/editors/form/types';

/**
 * Normalizes a raw form field value to a string suitable for indexing.
 * Each input type is handled as an explicit case to ensure exhaustiveness is enforced by TypeScript.
 * Returns an empty string when the value is absent.
 */
export function normalizeIndexValue(spec: IFormInputSpec, raw: unknown): string {
  if (raw === null || raw === undefined) {
    return '';
  }

  switch (spec.type) {
    // Primitive string fields
    case 'text':
    case 'date':
    case 'valueMap':
    case 'ifElse':
    case 'textExpression':
    case 'fieldUpdatedAt':
    case 'deviceProperty':
    case 'deviceEvidenceField':
    case 'datePlusPeriod':
    case 'faultType':
    case 'faultSeverity':
      return String(raw);

    // Numeric fields — stored as JS number
    case 'number':
    case 'sum':
      return typeof raw === 'number' ? raw.toFixed(spec.precision ?? 0) : String(raw);

    // Integer-like numeric fields
    case 'period':
    case 'numberOfMeasurements':
      return String(raw);

    // Aggregators — number or string depending on aggFunc
    case 'measurementColumnAggregator':
    case 'deviceFieldAggregator':
      return String(raw);

    // Boolean fields
    case 'checkbox':
    case 'comparator':
      return raw ? 'Áno' : 'Nie';

    // Constant — value type depends on spec.constantType
    case 'constant':
      return typeof raw === 'boolean' ? (raw ? 'Áno' : 'Nie') : String(raw);

    // Plain string select
    case 'select':
      return String(raw);

    // Rich text — strip HTML tags to produce plain text
    case 'wysiwyg': {
      return String(raw)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    // { value: string; label?: string } — store the human-readable label
    case 'safetyPoint': {
      const v = raw as { value?: string; label?: string };
      return v.label ?? v.value ?? '';
    }

    // { name: string; description?: string; primary: string } — primary is the display value
    case 'predefinedSelect': {
      const v = raw as { name?: string; description?: string; primary?: string };
      return v.primary ?? v.name ?? '';
    }

    // Array<{ name: string; description?: string; moreInfo?: string }>
    case 'predefinedList': {
      const items = raw as Array<{ name?: string; description?: string; moreInfo?: string }>;
      return items
        .map(({ name, description, moreInfo }) => [name, description, moreInfo].filter(Boolean).join(' '))
        .join(', ');
    }

    // Array<{ name: string; description?: string; manufacturer?: string; serialNumber?: string }>
    case 'measuringDevice': {
      const items = raw as Array<{
        name?: string;
        description?: string;
        manufacturer?: string;
        serialNumber?: string;
      }>;
      return items
        .map(({ name, manufacturer, serialNumber }) => [name, manufacturer, serialNumber].filter(Boolean).join(' '))
        .join(', ');
    }

    // { certificateNumber: string; technicianName?: string; ... }
    case 'certificate': {
      const v = raw as { certificateNumber?: string; technicianName?: string };
      return [v.certificateNumber, v.technicianName].filter(Boolean).join(' ');
    }

    // { qualificationNumber: string; companyName: string; city?: string; ... }
    case 'qualification': {
      const v = raw as { qualificationNumber?: string; companyName?: string; city?: string };
      return [v.qualificationNumber, v.companyName, v.city].filter(Boolean).join(' ');
    }

    // Array<{ fileId: string; fileName: string; fileType: string }>
    case 'fileList': {
      const files = raw as Array<{ fileName?: string }>;
      return files
        .map(({ fileName }) => fileName)
        .filter(Boolean)
        .join(', ');
    }

    default: {
      // TypeScript exhaustiveness check — if a new type is added to IFormInputSpec, this line
      // will produce a compile error until a case is added above.
      const _exhaustive: never = spec; // eslint-disable-line @typescript-eslint/no-unused-vars
      return String(raw);
    }
  }
}
